import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Users as UsersIcon, Trash2, UserCog, Crown, Shield, User as UserIcon, Wallet, Settings2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import RoleBadge, { ROLE_META, normalizeRole } from "@/components/RoleBadge";

// NEW MULTI-ROLE labels for the Feb 2026 refactor — independent from legacy role badges.
const NEW_ROLE_META = {
  super_admin:     { label: "Super Admin",     hint: "Komplette Konfiguration & Verwaltung",                  color: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700" },
  admin_operativo: { label: "Admin Operativo", hint: "Bearbeitung von Dokumenten, Policen, Provisionen",      color: "bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-700" },
  consulente:      { label: "Consulente",      hint: "Upload + Anagrafica (keine Doc/Premium-Bearbeitung)",    color: "bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700" },
  team_leader:     { label: "Team Leader",     hint: "Team-Reports & Koordination",                            color: "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700" },
  compliance:      { label: "Compliance",      hint: "Audits & Konformität",                                   color: "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700" },
};

const SCHEME_META = {
  p1_starter:      { label: "P1 · Starter",      color: "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-200" },
  p2_professional: { label: "P2 · Professional", color: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-300" },
  p3_senior:       { label: "P3 · Senior",       color: "bg-indigo-100 text-indigo-900 dark:bg-indigo-900/30 dark:text-indigo-300" },
  p4_partner:      { label: "P4 · Partner",      color: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300" },
};

const ROLE_ICONS = {
  master: Crown, control: Shield, basic: UserIcon, finanza: Wallet, admin: Shield,
};

export default function UsersPage() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editUser, setEditUser] = useState(null);  // user object for the roles+scheme edit dialog
  const [form, setForm] = useState({ email: "", password: "", full_name: "", role: "basic", team_leader_id: "" });
  const [assignBusy, setAssignBusy] = useState(false);

  const load = async () => {
    const [u, c, s] = await Promise.all([
      api.get("/users"),
      api.get("/clients"),
      api.get("/commission-schemes"),
    ]);
    setUsers(u.data); setClients(c.data); setSchemes(s.data || []);
  };
  useEffect(() => { load(); }, []);

  const saveRolesScheme = async (userId, rolesArr, schemeKey) => {
    try {
      await api.patch(`/users/${userId}`, { roles: rolesArr, commission_scheme: schemeKey });
      toast.success("Rollen & Schema gespeichert");
      setEditUser(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const createUser = async () => {
    if (!form.email || !form.password) { toast.error("E-Mail e password obbligatorie"); return; }
    try {
      const payload = { ...form };
      if (form.role !== "basic") payload.team_leader_id = null;
      if (!payload.team_leader_id) payload.team_leader_id = null;
      await api.post("/users", payload);
      toast.success("Utente creato");
      setOpenNew(false);
      setForm({ email: "", password: "", full_name: "", role: "basic", team_leader_id: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm(t.confirm_delete)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Gelöscht");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const updateRole = async (id, role) => {
    try {
      const patch = { role };
      // If switching away from basic, clear team_leader_id
      if (role !== "basic") patch.team_leader_id = null;
      await api.patch(`/users/${id}`, patch);
      toast.success("Rolle aggiornato");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const updateTeamLeader = async (id, teamLeaderId) => {
    try {
      await api.patch(`/users/${id}`, { team_leader_id: teamLeaderId || null });
      toast.success("Team aggiornato");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const assignClient = async (clientId, consultantId) => {
    setAssignBusy(true);
    try {
      await api.patch(`/clients/${clientId}/assign`, { consultant_id: consultantId || null });
      toast.success("Assegnato");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setAssignBusy(false); }
  };

  const controlUsers = useMemo(() => users.filter(u => u.id && normalizeRole(u.role) === "control"), [users]);
  const assignable = useMemo(
    () => users.filter(u => u.id && ["basic", "control", "master"].includes(normalizeRole(u.role))),
    [users]
  );

  // Group users by role for the directory
  const grouped = useMemo(() => {
    const g = { master: [], control: [], basic: [], finanza: [] };
    users.forEach(u => {
      const r = normalizeRole(u.role);
      (g[r] || g.basic).push(u);
    });
    return g;
  }, [users]);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase text-prime">/ {t.users}</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">Benutzerverwaltung</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Wertia Finanz Hierarchie: Master · Control · Basic · Finanzen
          </p>
        </div>
        <Button className="btn-prime gap-2" onClick={() => setOpenNew(true)} data-testid="new-user-btn">
          <UserPlus className="w-4 h-4" /> {t.create_user}
        </Button>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="role-legend">
        {Object.entries(ROLE_META).map(([role, meta]) => {
          const Icon = ROLE_ICONS[role];
          const count = grouped[role]?.length ?? 0;
          return (
            <div key={role} className={`prime-card p-4 border-l-4 ${meta.border}`} data-testid={`role-card-${role}`}>
              <div className="flex items-start justify-between mb-2">
                <div className={`w-9 h-9 rounded-md ${meta.bgSoft} ${meta.text} flex items-center justify-center`}>
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div className={`text-3xl font-display font-extrabold ${meta.text} leading-none`}>{count}</div>
              </div>
              <div className={`text-xs font-bold uppercase tracking-wider ${meta.text}`}>{meta.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{meta.description}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users directory */}
        <div className="prime-card">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <UserCog className="w-4 h-4 text-prime" />
            <h2 className="font-display font-bold text-xl">Team ({users.length})</h2>
          </div>
          <div className="divide-y divide-border max-h-[640px] overflow-auto">
            {["master", "control", "basic", "finanza"].flatMap(roleKey => {
              const list = grouped[roleKey] || [];
              if (!list.length) return [];
              const meta = ROLE_META[roleKey] || ROLE_META.basic;
              return [
                <div key={`hdr-${roleKey}`} className="px-6 py-2 bg-secondary/50 text-[10px] uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-2">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.bg}`} />
                  {meta.label} <span className="opacity-50">· {list.length}</span>
                </div>,
                ...list.map(u => {
                  const role = normalizeRole(u.role);
                  const rmeta = ROLE_META[role] || ROLE_META.basic;
                  return (
                    <div key={u.id} className="px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`user-row-${u.id}`}>
                      <div className={`w-9 h-9 rounded-full ${rmeta.bg} text-white flex items-center justify-center font-display text-xs shrink-0`}>
                        {(u.full_name || u.email).slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{u.full_name}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">{u.email}</div>
                      </div>
                      <Select value={role} onValueChange={(v) => updateRole(u.id, v)}>
                        <SelectTrigger className="h-8 w-28 text-xs" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(ROLE_META).map(([rkey, rm]) => (
                            <SelectItem key={rkey} value={rkey}>{rm.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {role === "basic" && (
                        <Select
                          value={u.team_leader_id || "__none"}
                          onValueChange={(v) => updateTeamLeader(u.id, v === "__none" ? null : v)}
                        >
                          <SelectTrigger className="h-8 w-36 text-xs" data-testid={`team-leader-select-${u.id}`}>
                            <SelectValue placeholder="Team Leader…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">— Kein team —</SelectItem>
                            {controlUsers.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {(role === "control" || role === "basic") && (
                        <FinmaInput user={u} onSaved={load} />
                      )}
                      <RoleBadge role={u.role} />
                      {/* NEW: Multi-role + Scheme display + edit */}
                      <div className="flex flex-wrap items-center gap-1 max-w-[260px]">
                        {(u.roles || []).map(r => (
                          <span key={r} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${NEW_ROLE_META[r]?.color || "bg-slate-100 text-slate-700"}`}
                                title={NEW_ROLE_META[r]?.hint || r}>
                            {NEW_ROLE_META[r]?.label || r}
                          </span>
                        ))}
                        {u.commission_scheme && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${SCHEME_META[u.commission_scheme]?.color || "bg-slate-100 text-slate-700"}`}
                                title="Provisionsschema">
                            {SCHEME_META[u.commission_scheme]?.label || u.commission_scheme}
                          </span>
                        )}
                        <button
                          onClick={() => setEditUser(u)}
                          className="text-muted-foreground hover:text-prime text-xs underline"
                          data-testid={`edit-roles-${u.id}`}
                          title="Rollen & Provisionsschema bearbeiten"
                        >
                          <Settings2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {role !== "master" && (
                        <button onClick={() => deleteUser(u.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                }),
              ];
            })}
          </div>
        </div>

        {/* Client assignment */}
        <div className="prime-card">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-prime" />
            <h2 className="font-display font-bold text-xl">{t.assigned_to}</h2>
          </div>
          {clients.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">{t.no_clients}</div>
          ) : (
            <div className="divide-y divide-border max-h-[640px] overflow-auto">
              {clients.map(c => (
                <div key={c.id} className="px-6 py-3 flex items-center gap-4" data-testid={`assign-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{c.email || c.document_number || "—"}</div>
                  </div>
                  <Select
                    value={c.consultant_id || "__none"}
                    onValueChange={v => assignClient(c.id, v === "__none" ? null : v)}
                    disabled={assignBusy}
                  >
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid={`assign-select-${c.id}`}>
                      <SelectValue placeholder={t.unassigned} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">{t.unassigned}</SelectItem>
                      {assignable.map(u => {
                        const ro = normalizeRole(u.role);
                        const rm = ROLE_META[ro] || ROLE_META.basic;
                        return (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name} · {rm.label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create user dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.create_user}</DialogTitle>
            <DialogDescription>Anlegen un nuovo membro del team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-uppercase text-muted-foreground">Vollständiger Name</Label>
              <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className="mt-1" data-testid="u-name" />
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">{t.email}</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="u-email" />
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">{t.password}</Label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="u-password" />
            </div>
            <div>
              <Label className="label-uppercase text-muted-foreground">{t.role}</Label>
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v, team_leader_id: v === "basic" ? form.team_leader_id : "" })}>
                <SelectTrigger className="mt-1" data-testid="u-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_META).map(([rkey, rm]) => (
                    <SelectItem key={rkey} value={rkey}>
                      <span className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${rm.bg}`} />
                        {rm.label} <span className="text-muted-foreground text-xs">— {rm.description}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.role === "basic" && (
              <div>
                <Label className="label-uppercase text-muted-foreground">Team Leader (CONTROL)</Label>
                <Select value={form.team_leader_id || "__none"} onValueChange={(v) => setForm({ ...form, team_leader_id: v === "__none" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="u-team-leader">
                    <SelectValue placeholder="— Kein team —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Kein team —</SelectItem>
                    {controlUsers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNew(false)}>{t.cancel}</Button>
            <Button className="btn-prime" onClick={createUser} data-testid="save-user-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Roles & Scheme dialog (NEW MULTI-ROLE SYSTEM) */}
      {editUser && (
        <EditRolesDialog
          key={editUser.id}
          user={editUser}
          schemes={schemes}
          onClose={() => setEditUser(null)}
          onSave={saveRolesScheme}
        />
      )}
    </div>
  );
}

function EditRolesDialog({ user, schemes, onClose, onSave }) {
  const [rolesArr, setRolesArr] = useState(user.roles || []);
  const [scheme, setScheme] = useState(user.commission_scheme || "p1_starter");
  const toggleRole = (r) => {
    setRolesArr(arr => arr.includes(r) ? arr.filter(x => x !== r) : [...arr, r]);
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-roles-dialog">
        <DialogHeader>
          <DialogTitle>Rollen & Provisionsschema</DialogTitle>
          <DialogDescription>
            <strong>{user.full_name}</strong> · <span className="font-mono">{user.email}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-uppercase text-muted-foreground">CRM-Rollen (Mehrfachauswahl)</Label>
            <div className="mt-2 space-y-2 border border-border rounded-md p-3 bg-secondary/20">
              {Object.entries(NEW_ROLE_META).map(([key, meta]) => (
                <label key={key} className="flex items-start gap-2 cursor-pointer" data-testid={`role-toggle-${key}`}>
                  <Checkbox checked={rolesArr.includes(key)} onCheckedChange={() => toggleRole(key)} />
                  <div className="-mt-0.5">
                    <div className="text-sm font-semibold">{meta.label}</div>
                    <div className="text-xs text-muted-foreground">{meta.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="label-uppercase text-muted-foreground">Provisionsschema (einer)</Label>
            <Select value={scheme} onValueChange={setScheme}>
              <SelectTrigger className="mt-1" data-testid="scheme-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(schemes || []).map(s => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label} — {s.consultant_pct}% Berater / {s.company_pct}% Wertia
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" />
              {(schemes || []).find(s => s.key === scheme)?.description || ""}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button className="btn-prime" onClick={() => onSave(user.id, rolesArr, scheme)} data-testid="save-roles-scheme-btn">
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FinmaInput({ user, onSaved }) {
  const [val, setVal] = useState(user.finma_register_nr || "");
  const [busy, setBusy] = useState(false);
  const dirty = (val || "") !== (user.finma_register_nr || "");
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { finma_register_nr: val });
      toast.success("FINMA-Nr. aggiornato");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5">
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="FINMA-Nr."
        className="h-8 w-28 text-xs font-mono"
        data-testid={`finma-input-${user.id}`}
      />
      {dirty && (
        <Button size="sm" className="h-8 px-2 btn-prime" onClick={save} disabled={busy}
                data-testid={`save-finma-${user.id}`}>
          Speichern
        </Button>
      )}
    </div>
  );
}
